/**
 * InvoiceBuilder (S1, Seth 2026-06-26) — the OFFICE invoice builder for a SERVICE
 * dispatch. The office fallback to the field app: complete the dispatch, key/override
 * the tech's hours (→ payroll), write tech notes (AI-assisted), build the billed
 * lines (diagnostic fee + pricebook parts), preview in the real Sedgwick template,
 * and (later, gated) email/text it. Anchors on the dispatch; saves a job-less
 * service invoice. Design: research/office-invoice-builder-design.md.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Trash2, Search, Eye, EyeOff, Loader2, FileText, Mail, MessageSquare, Clock,
  Sparkles, Check, X, ShieldCheck, Tag, BookOpen,
Printer,
} from "lucide-react";
import { useCommsPref } from "@/features/comms/comms";
import { PricebookBrowser } from "@/features/items/PricebookBrowser";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useItemCategories, memberEligibleSubtotal, memberDiscountByRule, memberDiscountEligible } from "@/lib/memberPricing";
import { useEntryFormKeys, useListNav, scrollActiveIntoView } from "@/lib/keys";
import { NotesEditor } from "@/components/NotesEditor";
import { TeamMemberSelect } from "@/components/TeamMemberSelect";
import { useActiveTechs } from "@/features/board/api";
import type { PendingTicket } from "@/features/board/queue";
import {
  useItemSearch, useSaveServiceInvoice, invoiceTotal, useSuggestParts, useMemberDiscount,
  useMemberDiagnostic, useAcceptedServiceQuote, useCustomerEmail,
  useDispatchDraftInvoice, useDispatchInvoiceLabor, isInvoiceConflict, saverLabel,
  type BuilderLine, type BuilderLabor, type PartSuggestion, type ItemHit, type DraftInvoice,
} from "./officeInvoice";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { usePlanPriceCatalog, useSellPlan, repairPctForTerm } from "@/features/agreements/planPricing";
import { useCustomerPlanOffers, offerState, offerMoney, fmtDay, type PlanOfferRow } from "@/features/agreements/planOffers";
import { usePlanDiscounts } from "@/features/customer-360/serviceTypes";

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// First maintenance check derived from the visit: the equipment worked on + the date.
// Furnace/heat/boiler → heating check; A/C/cooling → cooling check; otherwise fall back
// to the season (Apr–Sep = cooling, else heating). Matches the plan's tune-up template so
// Pegasus proposes it and the tech just confirms — no season/year to type.
function firstCheckCategory(serviceTypeName: string | null | undefined, dateISO: string): "heating" | "cooling" {
  const n = (serviceTypeName ?? "").toLowerCase();
  if (/furnace|heat|boiler/.test(n)) return "heating";
  if (/a\/?c\b|cool|condens|air ?cond/.test(n)) return "cooling";
  const m = Number(dateISO.slice(5, 7)); // 01–12
  return m >= 4 && m <= 9 ? "cooling" : "heating";
}
// Whether the visit itself was a tune-up/maintenance (→ the first check was done today).
function looksLikeTuneup(serviceTypeName: string | null | undefined): boolean {
  return /tune|mainten|precision|clean|check|inspect/i.test(serviceTypeName ?? "");
}
const CHECK_LABEL: Record<string, string> = { heating: "Furnace / heating check", cooling: "A/C / cooling check" };

export function InvoiceBuilder({
  dispatch: dispatchProp,
  customer,
  onClose,
}: {
  dispatch?: PendingTicket;
  customer?: { custno: string | null; locno: string | null; name: string | null; address?: string | null; city?: string | null };
  onClose: () => void;
}) {
  // Standalone = "New invoice" built straight off a customer, no dispatch behind it
  // (counter sale, product/membership-only sale, a correction). We synthesize a minimal
  // ticket so every existing `dispatch.*` read keeps working; the differences are: no
  // diagnostic seed, no "complete the dispatch," and the primary button is "Save & issue."
  const standalone = !dispatchProp;
  const dispatch: PendingTicket = dispatchProp ?? ({
    id: "",
    dispatch_no: null,
    custno: customer?.custno ?? null,
    locno: customer?.locno ?? null,
    customer_name: customer?.name ?? null,
    address: customer?.address ?? null,
    city: customer?.city ?? null,
    service_type_name: null,
    diag_fee: null,
    assigned_empno: null,
    scheduled_date: null,
  } as unknown as PendingTicket);
  const techs = useActiveTechs("03").data ?? [];
  const techName = (empno: string | null) => techs.find((t) => t.empno === empno)?.empname ?? empno ?? "";
  const save = useSaveServiceInvoice();

  const [technician, setTechnician] = useState<string | null>(dispatch.assigned_empno);
  // Salesperson is a live team-member pick (empno is the stable key; we keep the
  // resolved name to persist on the invoice's comfort_advisor text column).
  const [salespersonEmpno, setSalespersonEmpno] = useState<string | null>(null);
  const [salespersonName, setSalespersonName] = useState<string | null>(null);
  const [completedDate, setCompletedDate] = useState(dispatch.scheduled_date || todayISO());
  // A blank date completed must self-heal to today (Seth 2026-08-28: it
  // didn't auto-populate on his truck-issued invoice reopened in the office).
  useEffect(() => { if (!completedDate) setCompletedDate(todayISO()); }, [completedDate]);
  const [techNotes, setTechNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [discount, setDiscount] = useState(0);
  // A labeled discount the tech gave on the iPad ("Veteran discount") keeps
  // its name when the office saves.
  const [discountLabel, setDiscountLabel] = useState<string | null>(null);
  // Accepted SERVICE package → offer its lines for the bill (Seth 2026-06-30); dismiss once added/hidden.
  const [pkgAdded, setPkgAdded] = useState(false);
  // Enroll a non-member in a PLUS plan FROM this invoice (Seth 2026-06-30) — the
  // button that used to bounce uselessly to the 360. Picks a base plan + term.
  const [enroll, setEnroll] = useState<{ baseId: string; baseName: string; term: number; price: number; pct: number } | null>(null);
  // PLAN-DIAG-ENROLL: the offer this invoice fulfils — the homeowner's yes on the
  // repair quote (or an offer still waiting) — so the plan is attached, never sold twice.
  const [enrollOffer, setEnrollOffer] = useState<PlanOfferRow | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [pickBase, setPickBase] = useState("");
  const [pickTerm, setPickTerm] = useState(3);

  const [labor, setLabor] = useState<BuilderLabor[]>([
    { empno: dispatch.assigned_empno, name: techName(dispatch.assigned_empno), hours: 0, rate: 0 },
  ]);

  const [lines, setLines] = useState<BuilderLine[]>(() => {
    const seed: BuilderLine[] = [];
    if (dispatch.diag_fee != null) {
      seed.push({
        kind: "fee",
        description: `${dispatch.service_type_name ?? "Service"} — diagnostic`,
        qty: 1,
        unitPrice: Number(dispatch.diag_fee),
        print: true,
      });
    }
    return seed;
  });

  // INVOICE-LIVE-01 (Seth 2026-09-24): this window opens the SAME invoice the
  // tech is building on the iPad — never a blank one that would write over
  // their lines — and follows it live. Saving carries the version it was
  // built on; if the tech saved in between, the office picks: theirs or mine.
  const draftQ = useDispatchDraftInvoice(standalone ? null : dispatch.id, null);
  const laborQ = useDispatchInvoiceLabor(standalone ? null : dispatch.id);
  useRealtimeRefresh(["pegasus_job_invoice", "pegasus_job_invoice_line"], [["dispatch_draft_invoice"]]);
  const [seeded, setSeeded] = useState(standalone);
  const baseVersion = useRef<number | null>(standalone ? null : 0);
  const baseSig = useRef<string | null>(null);
  const seedAt = useRef(0);
  const [conflict, setConflict] = useState<{ by: string; via: "field" | "office" | null; version: number } | null>(null);
  const [liveNote, setLiveNote] = useState<string | null>(null);
  const applyServer = (d: DraftInvoice, lab: BuilderLabor[] | undefined) => {
    setTechNotes(d.tech_notes ?? "");
    setInternalNotes(d.internal_notes ?? "");
    if (d.completed_date) setCompletedDate(d.completed_date);
    if (d.technician) {
      const t = d.technician.trim().toLowerCase();
      const hit = techs.find((x) => (x.empname ?? "").trim().toLowerCase() === t)
        ?? techs.find((x) => (x.empname ?? "").trim().toLowerCase().split(/\s+/)[0] === t.split(/\s+/)[0]);
      if (hit) setTechnician(hit.empno);
    }
    if (d.comfort_advisor) { setSalespersonName(d.comfort_advisor); }
    // Discounts come back as their own negative lines: the member discount
    // recomputes itself; anything else is the "additional discount".
    const neg = d.lines.filter((l) => l.unitPrice < 0 && !/member discount|repair discount/i.test(l.description));
    const negSum = neg.reduce((t, l) => t + Math.abs((Number(l.unitPrice) || 0) * (Number(l.qty) || 1)), 0);
    setDiscount(Math.round(negSum * 100) / 100);
    setDiscountLabel(neg.length === 1 && !/^discount$/i.test(neg[0].description.trim()) ? neg[0].description : null);
    setLines(d.lines.filter((l) => l.unitPrice >= 0));
    if (lab && lab.length) setLabor(lab);
    baseVersion.current = d.version;
    baseSig.current = null;
    seedAt.current = Date.now();
  };
  useEffect(() => {
    if (seeded || draftQ.isLoading || laborQ.isLoading) return;
    if (draftQ.data) applyServer(draftQ.data, laborQ.data);
    else if (laborQ.data && laborQ.data.length) setLabor(laborQ.data);
    seedAt.current = Date.now();
    setSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQ.isLoading, laborQ.isLoading, seeded]);

  // Each line's description is a LIVE inventory search — type to find a part, pick to
  // autofill its price + link it to inventory; type something with no match and it
  // simply stays a custom line. No separate "add from pricebook" step to forget.
  const [searchIdx, setSearchIdx] = useState<number | null>(null);
  const [lineQ, setLineQ] = useState("");
  // PRICEBOOK-PICK-01: the full pricebook window (filters + details) — each pick is a part line.
  const [browseBook, setBrowseBook] = useState(false);
  // PRICEBOOK-PICK-01: "See all results and details" from one line's list — the pick fills THAT line.
  const [browseLine, setBrowseLine] = useState<{ idx: number; q: string } | null>(null);
  const lineItems = useItemSearch(lineQ).data ?? [];

  // Parts the AI pulled from the tech notes, matched to the pricebook — proposed
  // for the office to confirm onto the lines (never auto-added).
  const suggest = useSuggestParts();
  const [suggestions, setSuggestions] = useState<PartSuggestion[] | null>(null);
  // AUTO-suggest (Seth 2026-08-11: "I typed a description and thought it would
  // suggest inventory items… I was hoping something would happen"). Once the tech
  // notes settle (1.5s pause, real content), scan them for parts automatically —
  // proposals only, the office still confirms every line. Never re-runs for the
  // same text; the button remains for a manual re-scan.
  const lastSuggestedRef = useRef<string>("");
  const runSuggest = () => {
    if (!techNotes.trim()) return;
    lastSuggestedRef.current = techNotes.trim();
    suggest.mutate(techNotes, { onSuccess: (s) => setSuggestions(s) });
  };
  useEffect(() => {
    const n = techNotes.trim();
    if (n.length < 15 || n === lastSuggestedRef.current || suggest.isPending) return;
    const t = window.setTimeout(() => {
      lastSuggestedRef.current = n;
      suggest.mutate(n, { onSuccess: (s) => setSuggestions(s) });
    }, 1500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techNotes, suggest.isPending]);
  const addSuggestion = (s: PartSuggestion, i: number) => {
    addLine({ kind: "part", description: s.description, qty: s.qty, unitPrice: s.unitPrice, print: true, item_id: s.item_id });
    setSuggestions((prev) => (prev ? prev.filter((_, j) => j !== i) : null));
  };

  // Membership repair discount, derived from the customer's active agreements (never
  // a manual flag). Applies to the REPAIR work — the printed parts/labor/custom lines,
  // NOT the diagnostic/trip fee (members still pay the trip). It auto-recomputes as
  // lines change; the office can still add a separate courtesy discount on top.
  const member = useMemberDiscount(dispatch.custno).data;
  const acceptedPkg = useAcceptedServiceQuote(dispatch.custno, dispatch.locno).data;
  // PLUS plan catalog + per-term repair-discount tiers, for the invoice enroll.
  const planBases = (usePlanPriceCatalog().data ?? []).filter((p) => p.kind === "base" && p.active);
  const discountTiers = usePlanDiscounts().data;
  const sellPlan = useSellPlan();
  const customerOffers = useCustomerPlanOffers(dispatch.custno).data;
  // The plan waiting on this visit: accepted · unpaid with no invoice yet, else an
  // open offer on this call's repair quote (they can take it at the door).
  const pendingOffer = useMemo(() => {
    const rows = customerOffers ?? [];
    return rows.find((o) => offerState(o) === "accepted_unpaid" && !o.offer_invoice_id)
      ?? rows.find((o) => ["offered", "viewed"].includes(offerState(o)) && o.offered_on === "repair" && (!o.quote_dispatch_no || String(o.quote_dispatch_no) === String(dispatch.dispatch_no ?? "")))
      ?? null;
  }, [customerOffers, dispatch.dispatch_no]);
  // Customer email for sending the invoice (comms-gated): on file, or an ask if none.
  const custEmail = useCustomerEmail(dispatch.custno, dispatch.locno).data;
  // PAPER-INVOICE-01: paper-invoice customers get a banner here — the tech
  // never promises an email that the office will instead print and mail.
  const paperPref = useCommsPref(dispatch.custno).data;
  const [emailInput, setEmailInput] = useState("");
  // Seth 2026-08-11: "no way of changing or editing who the invoice emails go
  // to" — the on-file address is now editable in place.
  const [editingEmail, setEditingEmail] = useState(false);
  const sendEmail = editingEmail ? emailInput.trim() : (custEmail?.email ?? "").trim() || emailInput.trim();
  // First maintenance check derived from THIS visit — only used when enrolling a plan here.
  const firstCat = useMemo(() => firstCheckCategory(dispatch.service_type_name, completedDate), [completedDate]);
  const [firstCheckDone, setFirstCheckDone] = useState<boolean>(() => looksLikeTuneup(dispatch.service_type_name));
  const priceForTerm = (
    b: { p1: number | null; p2: number | null; p3: number | null; p5: number | null; p10: number | null } | undefined,
    term: number,
  ): number => {
    if (!b) return 0;
    const m: Record<number, number | null> = { 1: b.p1, 2: b.p2, 3: b.p3, 5: b.p5, 10: b.p10 };
    return Number(m[term] ?? 0);
  };
  // PLAN-DISC-SCOPE-01 (Seth 2026-10-28): the member % touches REPAIR PARTS AND
  // LABOR only — not fees, the plan line, equipment, accessories / IAQ, consumables
  // or materials (lib/memberPricing decides from the pricebook category; a line's
  // chip flips it by hand). The plan line is written as a custom line whose text
  // the rule already excludes, so nothing is subtracted twice.
  const lineItemCats = useItemCategories(lines.map((l) => l.item_id)).data;
  const repairSubtotal = useMemo(
    () => memberEligibleSubtotal(lines, lineItemCats),
    [lines, lineItemCats],
  );
  // The repair discount applies to a real member OR to a plan being enrolled on this invoice.
  const memberPct = member?.isMember ? (member.pct ?? 0) : (enroll?.pct ?? 0);
  const memberDiscount = useMemo(
    () => ((member?.isMember || enroll) ? Math.round(Math.max(0, repairSubtotal) * memberPct * 100) / 100 : 0),
    [member, enroll, memberPct, repairSubtotal],
  );
  const memberActive = !!(member?.isMember || enroll) && memberPct > 0;
  const billed = useMemo(() => invoiceTotal(lines), [lines]);
  const total = useMemo(
    () => Math.max(0, billed - memberDiscount - (Number(discount) || 0)),
    [billed, memberDiscount, discount],
  );
  // What a non-member WOULD save on this repair with the entry plan — the up-sell teaser.
  const wouldSave = useMemo(
    () => (member && !member.isMember ? Math.round(repairSubtotal * member.entryPct * 100) / 100 : 0),
    [member, repairSubtotal],
  );

  // Member DIAGNOSTIC repricing (Seth: the diagnostic isn't repair-discounted — a member
  // pays the PLUS diagnostic rate, e.g. $109 → $79). Auto-apply the PLUS price ONCE, only
  // for the unambiguous case: a seeded diagnostic line equal to the standard residential
  // rate. Commercial/downtown/after-hours diagnostics carry a context the dispatch doesn't
  // store, so they're left for the office to confirm rather than risk mis-pricing.
  const memberDiag = useMemberDiagnostic(dispatch.service_type_name, !!member?.isMember).data;
  const diagAppliedRef = useRef(false);
  useEffect(() => {
    if (diagAppliedRef.current || !memberDiag || memberDiag.plus >= memberDiag.standard) return;
    const idx = lines.findIndex(
      (l) => l.kind === "fee" && Math.abs((Number(l.unitPrice) || 0) - memberDiag.standard) < 0.005,
    );
    if (idx === -1) return;
    diagAppliedRef.current = true;
    setLines((ls) =>
      ls.map((l, j) =>
        j === idx
          ? { ...l, unitPrice: memberDiag.plus, description: /PLUS/i.test(l.description) ? l.description : `${l.description} · PLUS pricing` }
          : l,
      ),
    );
  }, [memberDiag, lines]);

  const setLine = (i: number, patch: Partial<BuilderLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = (l: BuilderLine) => setLines((ls) => [...ls, l]);
  // Drop the accepted service package's lines onto the bill in one tap — skip its
  // diagnostic if the invoice already seeded one (no double diagnostic).
  const addPackage = () => {
    if (!acceptedPkg) return;
    const hasFee = lines.some((l) => l.kind === "fee");
    const add = acceptedPkg.lines.filter((l) => !(hasFee && /diagnostic/i.test(l.description)));
    setLines((ls) => [
      ...ls,
      ...add.map((l) => ({ kind: (l.item_id ? "part" : "custom") as BuilderLine["kind"], description: l.description, qty: l.qty, unitPrice: l.unitPrice, print: true, item_id: l.item_id })),
    ]);
    setPkgAdded(true);
  };
  const confirmEnroll = () => {
    const base = planBases.find((b) => b.id === pickBase);
    if (!base) return;
    const price = priceForTerm(base, pickTerm);
    const pct = (discountTiers && discountTiers.length) ? repairPctForTerm(discountTiers, pickTerm) : (member?.entryPct ?? 0);
    setEnroll({ baseId: base.id, baseName: base.name, term: pickTerm, price, pct });
    addLine({ kind: "custom", description: `${base.name} — PLUS membership (${pickTerm} yr)`, qty: 1, unitPrice: price, print: true });
    setEnrollOpen(false);
  };
  // PLAN-DIAG-ENROLL: one tap puts the offered / accepted plan on this bill at the
  // price it was offered at, and remembers which offer it fulfils.
  const takeOffer = (o: PlanOfferRow) => {
    const base = planBases.find((b) => b.id === o.base_id);
    const price = Number(o.total) || priceForTerm(base, o.term);
    const pct = (discountTiers && discountTiers.length) ? repairPctForTerm(discountTiers, o.term) : (member?.entryPct ?? 0);
    const baseName = base?.name ?? o.base_name ?? "Maintenance Plus";
    setEnroll({ baseId: o.base_id ?? base?.id ?? "", baseName, term: o.term, price, pct });
    setEnrollOffer(o);
    addLine({ kind: "custom", description: `${baseName} — PLUS membership (${o.term} yr)`, qty: 1, unitPrice: price, print: true });
    setEnrollOpen(false);
  };
  const cancelEnroll = () => {
    if (enroll) {
      const desc = `${enroll.baseName} — PLUS membership (${enroll.term} yr)`;
      setLines((ls) => ls.filter((l) => l.description !== desc));
    }
    setEnrollOffer(null);
    setEnroll(null);
  };
  const rmLine = (i: number) => setLines((ls) => ls.filter((_, j) => j !== i));
  // KEYS-01b: the line grid works like the PO's — ↓ ↑ Enter on the pricebook
  // matches, Tab/Enter off the last line's price opens a fresh line, Enter
  // walks the header fields, Ctrl+Enter = Save & preview.
  const descRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusLine, setFocusLine] = useState<number | null>(null);
  useEffect(() => {
    if (focusLine == null) return;
    const el = descRefs.current[focusLine];
    if (el) { el.focus(); setFocusLine(null); }
  }, [focusLine, lines.length]);
  const addLineAndFocus = () => { setLines((ls) => { setFocusLine(ls.length); return [...ls, { kind: "custom", description: "", qty: 1, unitPrice: 0, print: true }]; }); };
  const lineListRef = useRef<HTMLDivElement>(null);
  const lineListOpen = searchIdx != null && lineQ.trim().length >= 2 && lineItems.length > 0;
  const lineNav = useListNav({
    count: lineListOpen ? lineItems.length : 0,
    open: lineListOpen,
    typed: lineQ.trim().length >= 2,
    onPick: (k) => { const it = lineItems[k]; const i = searchIdx; if (it && i != null) { setLine(i, { kind: "part", description: it.description ?? "", unitPrice: Number(it.base_price) || 0, item_id: it.id }); setSearchIdx(null); } },
    onClose: () => setSearchIdx(null),
  });
  useEffect(() => { if (lineListOpen) scrollActiveIntoView(lineListRef.current, lineNav.active); }, [lineListOpen, lineNav.active]);
  const addItem = (it: ItemHit, qty = 1) =>
    addLine({ kind: "part", description: it.description ?? "Item", qty, unitPrice: Number(it.base_price) || 0, print: true, item_id: it.id });

  // Resolve an ambiguous/unmatched suggestion by searching the inventory list.
  const [resolveIdx, setResolveIdx] = useState<number | null>(null);
  const [resolveQ, setResolveQ] = useState("");
  const resolveItems = useItemSearch(resolveQ).data ?? [];
  const openResolve = (i: number, seed: string) => { setResolveIdx(i); setResolveQ(seed); };
  const pickResolved = (it: ItemHit, sIdx: number, qty: number) => {
    addItem(it, qty);
    setSuggestions((prev) => (prev ? prev.filter((_, j) => j !== sIdx) : null));
    setResolveIdx(null); setResolveQ("");
  };

  // What this window would save — compared with the copy it opened (or last
  // reloaded) to know whether the office has unsaved edits.
  const sig = JSON.stringify([technician, salespersonName, completedDate, techNotes, internalNotes, lines, discount, labor]);
  useEffect(() => {
    if (!seeded) return;
    if (baseSig.current === null || Date.now() - seedAt.current < 2500) baseSig.current = sig;
  }, [sig, seeded]);
  const dirty = seeded && baseSig.current !== null && sig !== baseSig.current;
  // The tech saved a newer copy while this window is open.
  useEffect(() => {
    const d = draftQ.data;
    if (!seeded || !d || standalone || conflict) return;
    if (baseVersion.current != null && d.version <= baseVersion.current) return;
    if (!dirty) {
      applyServer(d, laborQ.data);
      setLiveNote(`Updated from ${saverLabel(d.updated_by, d.updated_via)} just now`);
      window.setTimeout(() => setLiveNote(null), 7000);
    } else {
      setConflict({ by: d.updated_by ?? "", via: d.updated_via, version: d.version });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQ.data?.version, seeded]);
  const loadTheirs = async () => {
    const [r, lab] = await Promise.all([draftQ.refetch(), laborQ.refetch()]);
    if (r.data) applyServer(r.data, lab.data);
    setConflict(null);
  };
  const keepMine = () => {
    if (conflict) baseVersion.current = conflict.version;
    setConflict(null);
  };

  // Three deliberate outcomes (Seth 2026-06-30): "draft" saves without completing the
  // dispatch (come back later); "preview" saves a draft and opens the printed invoice
  // (dispatch stays open); "complete" finalizes — completes the dispatch + enrolls any
  // membership. Only "complete" is irreversible-ish, so the membership sells only then.
  const onSave = async (mode: "draft" | "preview" | "complete") => {
    const complete = mode === "complete";
    let saved: { id: string; version: number };
    try {
    saved = await save.mutateAsync({
      dispatchId: standalone ? null : dispatch.id,
      dispatchNo: dispatch.dispatch_no,
      custno: dispatch.custno,
      locno: dispatch.locno,
      technician: technician ? techName(technician) : null,
      salesperson: salespersonName,
      completedDate: completedDate || todayISO(),
      techNotes,
      internalNotes,
      memberDiscount,
      memberDiscountLabel: member?.isMember
        ? `PLUS member discount (${Math.round(member.pct * 100)}%)`
        : enroll ? `New PLUS member discount (${Math.round(enroll.pct * 100)}%)` : undefined,
      discount,
      discountLabel: discountLabel ?? undefined,
      labor,
      lines,
      completeDispatch: complete,
      recipientEmail: sendEmail || null,
      saveEmailToRecord: !!emailInput.trim() && !custEmail?.onFile,
      expectedVersion: standalone ? null : baseVersion.current,
      via: "office",
    });
    } catch (e) {
      if (isInvoiceConflict(e)) { setConflict({ by: e.by, via: e.via, version: e.version }); return; }
      throw e;
    }
    const id = saved.id;
    baseVersion.current = saved.version;
    baseSig.current = sig;
    // Sell the membership only on Complete (so a draft/preview can't double-enroll).
    // custno guard narrows string|null → string and can't enroll a plan with no customer.
    // PLAN-DIAG-ENROLL: written as ACCEPTED on this invoice — payment activates it
    // (card, check, autopay consent, or the ESC posting coming back paid).
    if (complete && enroll && dispatch.custno) {
      try {
        await sellPlan.mutateAsync({
          custno: dispatch.custno, locno: dispatch.locno, term: enroll.term, baseId: enroll.baseId, baseName: enroll.baseName, total: enroll.price,
          invoiceId: id, via: "office",
          quoteId: enrollOffer?.offer_quote_id ?? acceptedPkg?.quoteId ?? null,
          saleId: enrollOffer?.sale_id ?? null,
          firstCheck: { category: firstCat, done: firstCheckDone },
        });
      }
      catch (e) { console.error("membership enroll failed:", e); }
    }
    if (mode !== "draft") window.open(`/invoices/${id}`, "_blank");
    onClose();
  };

  const onFormKeys = useEntryFormKeys(() => { if (!save.isPending) void onSave("preview"); });

  // Design pass (Seth 2026-08-11: "larger and better looking… most modern app"):
  // one set of tokens sizes every field — roomier inputs, readable labels.
  const input = "w-full rounded-lg border border-hairline px-3 py-2.5 text-[15px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
  const label = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500";

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      icon={<FileText className="h-4 w-4 text-brand-500" />}
      title={standalone ? "New invoice" : "Complete & invoice"}
      subtitle={[dispatch.customer_name, [dispatch.address, dispatch.city].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-slate-500">Customer total <span className="ml-1.5 text-2xl font-bold tracking-tight text-slate-900">{money(total)}</span></div>
          <div className="flex flex-wrap items-center gap-2">
            <span title="Sending is paused until the comms gate is turned on at go-live" className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-slate-300"><Mail className="h-3.5 w-3.5" /> Email</span>
            <span title="Sending is paused until the comms gate is turned on at go-live" className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-slate-300"><MessageSquare className="h-3.5 w-3.5" /> Text</span>
            <Button variant="ghost" size="sm" loading={save.isPending} onClick={() => onSave("draft")} title={standalone ? "Save as a draft — finish it later" : "Save without completing — the dispatch stays open so you can come back to it"}>Save draft</Button>
            <Button variant="secondary" size="sm" loading={save.isPending} onClick={() => onSave("preview")} title="Ctrl+Enter">
              <FileText className="h-3.5 w-3.5" /> Save &amp; preview
            </Button>
            <Button variant="primary" size="sm" loading={save.isPending} onClick={() => onSave("complete")}>
              <Check className="h-3.5 w-3.5" /> {standalone ? "Save & issue" : "Complete & invoice"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5" data-entry-form onKeyDown={onFormKeys}>
        {/* INVOICE-LIVE-01 — whose copy this is, live. */}
        {conflict ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="text-sm font-semibold text-amber-900">{saverLabel(conflict.by, conflict.via)} changed this invoice while you were editing.</p>
            <p className="mt-0.5 text-xs text-amber-800">Load their version (your unsaved changes here are dropped), or keep yours and save over theirs.</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => void loadTheirs()}>Load Their Version</Button>
              <Button size="sm" variant="secondary" onClick={keepMine}>Keep Mine</Button>
            </div>
          </div>
        ) : !standalone && draftQ.data ? (
          <LiveStrip d={draftQ.data} note={liveNote} dirty={dirty} />
        ) : null}

        {/* PAPER-INVOICE-01: the customer gets PAPER — completing this invoice
            drops it in the office To-Mail queue automatically; invoice emails
            are suppressed for them. */}
        {paperPref?.paper_invoice && (
          <div className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-900">
            <Printer className="h-4 w-4 shrink-0 text-brand-600" />
            Paper invoice — the office prints &amp; mails this one. It lands on their To-Mail list the moment you complete it; no email goes out.
          </div>
        )}

        {/* Header meta */}
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block"><span className={label}>Technician</span>
            <select className={input} value={technician ?? ""} onChange={(e) => setTechnician(e.target.value || null)}>
              <option value="">Unassigned</option>
              {techs.map((t) => <option key={t.empno} value={t.empno}>{t.empname ?? t.empno}</option>)}
            </select>
          </label>
          <label className="block"><span className={label}>Salesperson (optional)</span>
            <TeamMemberSelect
              value={salespersonEmpno}
              onChange={(empno, name) => { setSalespersonEmpno(empno); setSalespersonName(name); }}
              blankLabel="— add if needed —"
            />
          </label>
          <label className="block"><span className={label}>{standalone ? "Invoice date" : "Completed"}</span>
            <input type="date" className={input} value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} />
          </label>
        </div>

        {/* Labor → payroll */}
        <section>
          <div className="mb-1.5 flex items-center justify-between">
            <span className={label}><Clock className="mr-1 inline h-3.5 w-3.5" /> Labor hours <span className="font-normal normal-case text-slate-400">— logs to payroll</span></span>
            <button type="button" onClick={() => setLabor((l) => [...l, { empno: null, name: "", hours: 0, rate: 0 }])} className="inline-flex items-center gap-1 rounded-lg border border-hairline px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-700"><Plus className="h-3.5 w-3.5" /> Add tech</button>
          </div>
          <div className="space-y-1.5">
            {labor.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <select className={cn(input, "flex-1")} value={row.empno ?? ""} onChange={(e) => setLabor((l) => l.map((r, j) => j === i ? { ...r, empno: e.target.value || null, name: techName(e.target.value) } : r))}>
                  <option value="">Select tech</option>
                  {techs.map((t) => <option key={t.empno} value={t.empno}>{t.empname ?? t.empno}</option>)}
                </select>
                <input type="number" step="0.25" min="0" className={cn(input, "w-24")} value={row.hours || ""} placeholder="hours" onChange={(e) => setLabor((l) => l.map((r, j) => j === i ? { ...r, hours: Number(e.target.value) } : r))} />
                <span className="text-xs text-slate-400">hrs</span>
                {labor.length > 1 && <button type="button" tabIndex={-1} title="Remove" onClick={() => setLabor((l) => l.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        </section>

        {/* Tech notes (AI) */}
        <section>
          <div className={label}>Tech notes <span className="font-normal normal-case text-slate-400">— printed on the customer invoice</span></div>
          <NotesEditor value={techNotes} onChange={setTechNotes} rows={3} placeholder="What did we find + do… (type or Dictate, then Clean up)" />
        </section>

        <section>
          <div className={label}>Internal notes <span className="font-normal normal-case text-slate-400">— office only, never printed</span></div>
          <NotesEditor value={internalNotes} onChange={setInternalNotes} rows={2} placeholder="Anything the office should know — not shown to the customer…" />
        </section>

        {/* Line items */}
        <section>
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <span className={label}>
              Line items
              <InfoTip label="What the crossed-out eye means" className="ml-1.5">A crossed-out eye <EyeOff className="inline h-3 w-3 text-amber-400" /> marks an internal-only line: it&apos;s hidden from the customer and left out of the total.</InfoTip>
            </span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={!techNotes.trim() || suggest.isPending} onClick={runSuggest} title={techNotes.trim() ? "Scan the tech notes for parts" : "Write tech notes first"} className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50/60 px-3 py-1.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100 disabled:border-hairline disabled:bg-transparent disabled:text-slate-300">
                {suggest.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {suggest.isPending ? "Scanning notes…" : "Suggest from notes"}
              </button>
              <button type="button" onClick={() => setBrowseBook(true)} title="Open the pricebook — filters and details" className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"><BookOpen className="h-4 w-4" /> Browse Pricebook</button>
              <button type="button" onClick={addLineAndFocus} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-800"><Plus className="h-4 w-4" /> Add line</button>
            </div>
          </div>
          <PricebookBrowser open={browseBook} onClose={() => setBrowseBook(false)} pickLabel="Add to Invoice"
            onPick={(it) => addLine({ kind: "part", description: it.description ?? it.part_number ?? "Part", qty: 1, unitPrice: Number(it.base_price) || 0, print: true, item_id: it.id })} />
          <PricebookBrowser open={browseLine != null} initialQuery={browseLine?.q ?? ""} multi={false} pickLabel="Use on This Line"
            onClose={() => setBrowseLine(null)}
            onPick={(it) => { if (browseLine) setLine(browseLine.idx, { kind: "part", description: it.description ?? it.part_number ?? "Part", unitPrice: Number(it.base_price) || 0, item_id: it.id }); }} />

          {/* Accepted SERVICE package (Seth 2026-06-30) — the customer already picked this
              Good/Better/Best option; offer to drop its lines onto the bill in one tap.
              Emerald (a real acceptance) vs the amber AI-from-notes guess below. */}
          {acceptedPkg && acceptedPkg.lines.length > 0 && !pkgAdded && (
            <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50/70 p-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-900">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Customer accepted the {acceptedPkg.tier ? acceptedPkg.tier.charAt(0).toUpperCase() + acceptedPkg.tier.slice(1) : (acceptedPkg.optionLabel ?? "service")} package
                  {acceptedPkg.acceptedAt ? ` · ${new Date(acceptedPkg.acceptedAt).toLocaleDateString()}` : ""} — {acceptedPkg.lines.length} line{acceptedPkg.lines.length === 1 ? "" : "s"} · {money(acceptedPkg.total)}
                </span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={addPackage} className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-2 py-1 text-xs font-semibold text-white transition-colors hover:bg-emerald-800"><Check className="h-3 w-3" /> Add to invoice</button>
                  <button type="button" onClick={() => setPkgAdded(true)} aria-label="Dismiss" className="rounded p-0.5 text-emerald-700 hover:bg-emerald-100"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          )}

          {/* AI part suggestions from the tech notes — propose, office confirms. */}
          {suggestions && (
            <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900"><Sparkles className="h-3.5 w-3.5" /> {suggestions.length ? "Parts found in the notes — confirm to add" : "No parts found in the notes"}</span>
                <div className="flex items-center gap-2">
                  {suggestions.length > 1 && <button type="button" onClick={() => { suggestions.forEach((s) => addLine({ kind: "part", description: s.description, qty: s.qty, unitPrice: s.unitPrice, print: true, item_id: s.item_id })); setSuggestions(null); }} className="text-xs font-semibold text-amber-800 hover:underline">Add all</button>}
                  <button type="button" onClick={() => setSuggestions(null)} aria-label="Dismiss" className="rounded p-0.5 text-amber-700 hover:bg-amber-100"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="space-y-1">
                {suggestions.map((s, i) => (
                  <div key={i} className="rounded-md bg-white">
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="font-medium text-slate-800">{s.description}</span>
                          {s.recommended && <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">recommended</span>}
                          {!s.matched && <span className="rounded bg-slate-100 px-1 text-[10px] font-medium text-slate-500">not in pricebook</span>}
                        </div>
                        {(s.part_number || s.model || s.reason) && (
                          <div className="truncate text-[11px] text-slate-400">
                            {[s.part_number, s.model].filter(Boolean).join(" · ")}
                            {(s.part_number || s.model) && s.reason ? " — " : ""}
                            {s.reason}
                          </div>
                        )}
                      </div>
                      <span className="shrink-0 text-xs text-slate-500">{s.qty} × {money(s.unitPrice)}</span>
                      <button type="button" onClick={() => (resolveIdx === i ? setResolveIdx(null) : openResolve(i, s.name))} title="Search the inventory list for the exact part" className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"><Search className="h-3 w-3" /> Find part</button>
                      <button type="button" onClick={() => addSuggestion(s, i)} className="inline-flex shrink-0 items-center gap-1 rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-800"><Check className="h-3 w-3" /> Add</button>
                    </div>
                    {resolveIdx === i && (
                      <div className="border-t border-amber-100 px-2 py-2">
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                          <input autoFocus className={cn(input, "pl-8")} value={resolveQ} onChange={(e) => setResolveQ(e.target.value)} placeholder="Search inventory by model, part #, manufacturer…" />
                        </div>
                        {resolveQ.trim().length >= 2 && (
                          <div className="mt-1 max-h-40 overflow-auto">
                            {resolveItems.length === 0 ? <div className="px-2 py-2 text-center text-[11px] text-slate-400">No inventory match — tap Add to keep it as a manual line, then set the price</div> : resolveItems.map((it) => (
                              <button key={it.id} type="button" onClick={() => pickResolved(it, i, s.qty)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-amber-50">
                                <span className="min-w-0"><span className="block truncate">{it.description}</span><span className="block truncate text-[11px] text-slate-400">{[it.manufacturer, it.model, it.part_number].filter(Boolean).join(" · ") || "no model / part #"}{it.qty_in_stock != null ? ` · ${it.qty_in_stock} in stock` : ""}</span></span>
                                <span className="shrink-0 font-medium">{money(Number(it.base_price) || 0)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-visible rounded-lg border border-hairline">
            {lines.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <p className="text-sm text-slate-400">No lines yet — every line's description searches the pricebook as you type.</p>
                <button type="button" onClick={addLineAndFocus} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-800"><Plus className="h-4 w-4" /> Add the first line</button>
              </div>
            ) : lines.map((l, i) => (
              <div key={i} className={cn("flex items-center gap-2 border-b border-hairline px-2.5 py-2 last:border-b-0", !l.print && "bg-slate-50/70")}>
                <div className="relative flex-1">
                  <input
                    ref={(el) => { descRefs.current[i] = el; }}
                    role="combobox" aria-expanded={searchIdx === i && lineListOpen} aria-autocomplete="list" autoComplete="off"
                    className={cn(input, "w-full")}
                    value={l.description}
                    placeholder="Type a part, model, or part # — or a custom description"
                    onChange={(e) => { setLine(i, { description: e.target.value }); setSearchIdx(i); setLineQ(e.target.value); }}
                    onFocus={(e) => { setSearchIdx(i); setLineQ(l.description); e.target.select(); }}
                    onKeyDown={(e) => { if (searchIdx === i) lineNav.onKeyDown(e); }}
                    onBlur={() => window.setTimeout(() => setSearchIdx((cur) => (cur === i ? null : cur)), 150)}
                  />
                  {searchIdx === i && lineListOpen && (
                    <div ref={lineListRef} role="listbox" data-glass="panel" className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-auto rounded-xl">
                      {lineItems.map((it, k) => (
                        <button key={it.id} type="button" role="option" aria-selected={lineNav.active === k} data-index={k} tabIndex={-1}
                          onMouseDown={(e) => { e.preventDefault(); setLine(i, { kind: "part", description: it.description ?? "", unitPrice: Number(it.base_price) || 0, item_id: it.id }); setSearchIdx(null); }}
                          onMouseEnter={() => lineNav.setActive(k)}
                          className={cn("flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-sm", lineNav.active === k ? "bg-brand-50" : "hover:bg-brand-50")}>
                          <span className="min-w-0"><span className="block truncate">{it.description}</span><span className="block truncate text-[11px] text-slate-400">{[it.manufacturer, it.model, it.part_number].filter(Boolean).join(" · ") || "no model / part #"}{it.qty_in_stock != null ? ` · ${it.qty_in_stock} in stock` : ""}</span></span>
                          <span className="shrink-0 font-medium">{money(Number(it.base_price) || 0)}</span>
                        </button>
                      ))}
                      <button type="button" tabIndex={-1}
                        onMouseDown={(e) => { e.preventDefault(); setBrowseLine({ idx: i, q: lineQ }); setSearchIdx(null); }}
                        className="flex w-full items-center justify-between border-t border-slate-100 px-2.5 py-1.5 text-left text-sm font-semibold text-brand-700 hover:bg-brand-50">
                        See all results and details <BookOpen className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <input type="number" step="1" min="0" className={cn(input, "w-16 text-center")} value={l.qty || ""} onFocus={(e) => e.target.select()} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} />
                <span className="text-slate-300">×</span>
                <div className="relative w-28"><span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><input type="number" step="0.01" min="0" className={cn(input, "pl-5 text-right")} value={l.unitPrice || ""} onFocus={(e) => e.target.select()} onChange={(e) => setLine(i, { unitPrice: Number(e.target.value) })}
                  onKeyDown={(e) => {
                    // Off the last line's price with a description on it → a fresh line, cursor in its description.
                    if ((e.key === "Tab" && !e.shiftKey) || (e.key === "Enter" && !e.ctrlKey && !e.metaKey)) {
                      if (i === lines.length - 1 && l.description.trim()) { e.preventDefault(); addLineAndFocus(); }
                    }
                  }} /></div>
                <span className="w-20 text-right text-sm font-medium">{money((Number(l.qty) || 0) * (Number(l.unitPrice) || 0))}</span>
                {/* PLAN-DISC-SCOPE-01: does the member % touch this line? The rule decides
                    (repair parts + labor yes; equipment, IAQ, consumables, fees, the plan
                    line no); a tap flips it for this line. */}
                {memberActive && l.kind !== "fee" && l.print && (Number(l.unitPrice) || 0) > 0 ? (() => {
                  const it = l.item_id ? lineItemCats?.[l.item_id] : null;
                  const byRule = memberDiscountByRule(l, it);
                  const on = memberDiscountEligible(l, it);
                  return (
                    <button type="button" tabIndex={-1}
                      title={on ? `Member ${Math.round(memberPct * 100)}% applies to this line — tap to charge list price` : "At list price — the member % doesn't touch equipment, IAQ products, consumables or fees. Tap to apply it anyway."}
                      onClick={() => setLine(i, { memberDiscount: on ? false : true })}
                      className={cn("w-11 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
                        on ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500",
                        on !== byRule && "ring-1 ring-inset ring-slate-400/60")}>
                      {on ? `${Math.round(memberPct * 100)}%` : "list"}
                    </button>
                  );
                })() : memberActive ? <span className="w-11 shrink-0" /> : null}
                <button type="button" tabIndex={-1} title={l.print ? "Shown on the customer invoice — click to make internal" : "Internal only — click to show on the invoice"} onClick={() => setLine(i, { print: !l.print })} className={cn("rounded p-1", l.print ? "text-slate-400 hover:text-slate-700" : "text-amber-500")}>{l.print ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                <button type="button" tabIndex={-1} title="Remove this line" onClick={() => rmLine(i)} className="rounded p-1 text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          {/* Membership: auto-applied repair discount for members, or an honest
              up-sell teaser for non-members (the "how much they'd have saved"). */}
          {member?.isMember ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800">
                <ShieldCheck className="h-4 w-4" /> {member.planLabel} — {Math.round(member.pct * 100)}% off repair parts &amp; labor
              </span>
              <span className="text-sm font-semibold text-emerald-800">−{money(memberDiscount)}</span>
            </div>
          ) : enroll ? (
            <div className="mt-3 rounded-lg border border-violet-300 bg-violet-50/70 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-900">
                  <ShieldCheck className="h-4 w-4 text-violet-600" /> Enrolling in {enroll.baseName} · {enroll.term} yr — {Math.round(enroll.pct * 100)}% off repair parts &amp; labor · on this bill, active when it is paid
                </span>
                <button type="button" onClick={cancelEnroll} className="shrink-0 text-xs font-semibold text-violet-700 hover:underline">Remove</button>
              </div>
              {/* First maintenance check — Pegasus derives it from this visit; the tech just
                  confirms. Done → counts as check 1 (won't nag to schedule it); the rest
                  auto-schedule by season. */}
              <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-violet-100 pt-2 text-xs text-violet-900">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={firstCheckDone} onChange={(e) => setFirstCheckDone(e.target.checked)} className="h-3.5 w-3.5 rounded border-violet-300" />
                  <span>First check — <b>{CHECK_LABEL[firstCat]}</b>, {new Date(completedDate + "T00:00:00").getFullYear()} — done on this visit</span>
                </label>
                <InfoTip label="How the first check is scheduled">Checked, it counts as check 1 and the rest schedule themselves by season. Unchecked, we&apos;ll schedule the first check for the coming season.</InfoTip>
              </div>
            </div>
          ) : member ? (
            <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2">
              {/* PLAN-DIAG-ENROLL: the plan they already said yes to (or were offered) on the repair quote. */}
              {pendingOffer && (
                <div className={cn("mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border px-2.5 py-1.5", offerState(pendingOffer) === "accepted_unpaid" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50/70")}>
                  <span className={cn("inline-flex items-center gap-1.5 text-sm", offerState(pendingOffer) === "accepted_unpaid" ? "text-amber-900" : "text-emerald-900")}>
                    <ShieldCheck className="h-4 w-4" />
                    {offerState(pendingOffer) === "accepted_unpaid"
                      ? <>Said yes to the plan {pendingOffer.accepted_at ? fmtDay(pendingOffer.accepted_at) : ""} — <b>{pendingOffer.plan_name.replace(/^Sedgwick Heating\s+/i, "")} · {pendingOffer.term} yr · {offerMoney(pendingOffer.total)}</b></>
                      : <>Plan offered on the repair quote — <b>{pendingOffer.plan_name.replace(/^Sedgwick Heating\s+/i, "")} · {pendingOffer.term} yr · {offerMoney(pendingOffer.total)}</b></>}
                  </span>
                  <button type="button" onClick={() => takeOffer(pendingOffer)} title="Puts the plan on this bill at the offered price; it activates when the invoice is paid"
                    className={cn("shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold text-white transition-colors", offerState(pendingOffer) === "accepted_unpaid" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700")}>
                    Add it to this bill
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm text-violet-900">
                  <Tag className="h-4 w-4 text-violet-500" /> Not a PLUS member{repairSubtotal > 0 ? <> — they&apos;d save <span className="font-semibold">{money(wouldSave)}</span> ({Math.round(member.entryPct * 100)}%) on this repair with a plan</> : null}.
                </span>
                <button type="button" onClick={() => setEnrollOpen((o) => !o)} className="shrink-0 rounded-md bg-violet-700 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-violet-800">Add PLUS membership</button>
              </div>
              {enrollOpen && (
                <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-violet-100 pt-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">Plan
                    <select value={pickBase} onChange={(e) => setPickBase(e.target.value)} className="mt-0.5 block rounded-md border border-violet-200 px-2 py-1 text-sm">
                      <option value="">Choose…</option>
                      {planBases.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </label>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">Term
                    <select value={pickTerm} onChange={(e) => setPickTerm(Number(e.target.value))} className="mt-0.5 block rounded-md border border-violet-200 px-2 py-1 text-sm">
                      {[1, 2, 3, 5, 10].map((t) => <option key={t} value={t}>{t} yr</option>)}
                    </select>
                  </label>
                  <div className="pb-1 text-sm font-semibold text-violet-900">{pickBase ? money(priceForTerm(planBases.find((b) => b.id === pickBase), pickTerm)) : "—"}<span className="ml-1 text-[11px] font-normal text-violet-500">membership</span></div>
                  <button type="button" disabled={!pickBase} onClick={confirmEnroll} className="rounded-md bg-violet-700 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-violet-800 disabled:opacity-50">Enroll &amp; add to invoice</button>
                </div>
              )}
            </div>
          ) : null}

          {/* Totals breakdown: subtotal → member discount → courtesy discount → total. */}
          <div className="mt-3 ml-auto w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex items-center justify-between text-slate-500"><span>Subtotal</span><span className="font-medium text-slate-700">{money(billed)}</span></div>
            {memberDiscount > 0 && (
              <div className="flex items-center justify-between text-emerald-700"><span>Member discount</span><span>−{money(memberDiscount)}</span></div>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-500">Additional discount</span>
              <div className="relative w-28"><span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">−$</span><input type="number" min="0" step="0.01" className={cn(input, "pl-7 text-right")} value={discount || ""} onChange={(e) => setDiscount(Number(e.target.value))} placeholder="0.00" /></div>
            </div>
            <div className="flex items-center justify-between border-t border-hairline pt-2 text-lg font-bold tracking-tight text-slate-900"><span>Total</span><span>{money(total)}</span></div>
          </div>

          {/* Email the invoice (sends once the comms gate is on at go-live). Pre-filled when
              we have an address; an ask when we don't — a captured email saves to the customer
              record for next time (Pegasus-native; ESC stays read-only). */}
          <div className="mt-3 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-sm">
            {custEmail?.onFile && !editingEmail ? (
              <div className="flex flex-wrap items-center gap-2 text-slate-600">
                <Mail className="h-4 w-4 text-slate-400" />
                <span>Invoice emails to <span className="font-medium text-slate-800">{custEmail.email}</span></span>
                <span className="text-[11px] text-slate-400">· on file</span>
                <button type="button" onClick={() => { setEmailInput(custEmail.email ?? ""); setEditingEmail(true); }} className="ml-auto rounded-md border border-hairline px-2 py-0.5 text-xs font-medium text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-700">Change</button>
              </div>
            ) : custEmail?.onFile && editingEmail ? (
              <label className="block">
                <span className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Mail className="h-3.5 w-3.5" /> Email invoice to <button type="button" onClick={() => { setEditingEmail(false); setEmailInput(""); }} className="ml-1 font-normal normal-case text-brand-600 hover:underline">use the one on file</button></span>
                <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="name@email.com" className={input} />
              </label>
            ) : (
              <label className="block">
                <span className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Mail className="h-3.5 w-3.5" /> Email invoice to <span className="font-normal normal-case text-amber-600">— none on file</span></span>
                <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="name@email.com — saved to the customer record" className={input} />
              </label>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

/** INVOICE-LIVE-01: "Live from Terry's iPad · saved 40 sec ago". */
function LiveStrip({ d, note, dirty }: { d: DraftInvoice; note: string | null; dirty: boolean }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = window.setInterval(() => tick((n) => n + 1), 15000); return () => window.clearInterval(t); }, []);
  const ago = (() => {
    if (!d.updated_at) return null;
    const secs = Math.max(0, Math.round((Date.now() - new Date(d.updated_at).getTime()) / 1000));
    if (secs < 60) return `${Math.max(5, Math.round(secs / 5) * 5)} sec ago`;
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins} min ago`;
    return new Date(d.updated_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  })();
  const fromField = d.updated_via === "field";
  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs",
      fromField ? "border-sky-200 bg-sky-50/70 text-sky-900" : "border-hairline bg-slate-50/70 text-slate-600")}>
      <span className="inline-flex items-center gap-1.5 font-semibold">
        {fromField && <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" /></span>}
        {fromField ? `Live from ${saverLabel(d.updated_by, "field")}` : `Last saved by ${saverLabel(d.updated_by, d.updated_via)}`}
        {ago ? <span className="font-normal opacity-75">· {ago}</span> : null}
        <InfoTip label="How this stays in sync">Changes from the iPad show up here on their own. Yours reach the iPad when you save.</InfoTip>
      </span>
      {/* A live update, or the office's unsaved changes, stay in plain sight. */}
      {(note || dirty) && <span className="opacity-80">{note ?? "Your changes reach the iPad when you save."}</span>}
      {d.status === "issued" && <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">Finished on the truck</span>}
    </div>
  );
}
